<script setup lang="ts">
    import { ref } from 'vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import { __ } from '@/js/stores/expressions';
    import { useMutationSeedAllTenantData } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { DataSeedingResponse } from '@/js/services/api/backOffice/types';
    import { TriangleAlertIcon } from '@lucide/vue';

    const { mutateAsync: seedAllTenantData, isPending } = useMutationSeedAllTenantData();

    const result = ref<DataSeedingResponse | null>(null);

    const seed = async () => {
        result.value = null;
        result.value = await seedAllTenantData();
    };

    const hasProblems = (response: DataSeedingResponse) =>
        Object.keys(response.problems).length > 0;
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-data-seeding') }}</h1>
        </template>
        <template #content>
            <EchoButton variant="primary" :loading="isPending" @click="seed">
                {{ __('back-office-seed-all-tenant-data') }}
            </EchoButton>

            <div v-if="result" class="mt-8">
                <p v-if="!hasProblems(result)" class="text-body-bold text-sentiment-positive">
                    {{ __('back-office-seed-all-tenant-data-success') }}
                </p>

                <div v-else>
                    <p class="text-body-bold text-sentiment-negative mb-4">
                        {{ __('back-office-seed-all-tenant-data-problems') }}
                    </p>
                    <div
                        v-for="(problems, category) in result.problems"
                        :key="category"
                        class="mb-4"
                    >
                        <template v-if="problems.length > 0">
                            <div class="border-t border-gray-light pt-3 flex">
                                <div class="mr-1.5 pt-0.25 text-sentiment-negative">
                                    <TriangleAlertIcon class="size-5" />
                                </div>
                                <div>
                                    <h3 class="heading-sm flex items-center">
                                        {{ category }}
                                    </h3>
                                    <ul class="list-disc pl-6 mt-1">
                                        <li
                                            v-for="(problem, index) in problems"
                                            :key="index"
                                            class="text-body"
                                        >
                                            {{ problem }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </template>
    </SettingsPageLayout>
</template>
